import { Fragment, ReactElement, useCallback, useEffect, useRef } from "react";
import { Text } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { DEF_EDGE_THICKNESS } from "./constants";
import {
  EdgeLabel,
  EdgeLabelObject,
  EdgeLabelWrapper,
  SVGContainer,
} from "./style_vanilla.css";
import { setSVGbounds, setNodeEdges } from "./util";
import type { EdgeConfig, ElementRegistry, TreeConfig } from "./types";

type TreeEdgeProps = {
  edgeConfigs: Array<EdgeConfig>;
  treeConfig: TreeConfig;
  containerBounds?: DOMRect | null;
  instanceId: string;
  elementRegistry: ElementRegistry;
};

export const TreeEdge = ({
  edgeConfigs,
  treeConfig,
  containerBounds,
  instanceId,
  elementRegistry,
}: TreeEdgeProps): ReactElement => {
  const svgElement = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      if (containerBounds && svgElement.current) {
        setSVGbounds(containerBounds, svgElement.current);
        setNodeEdges(containerBounds, edgeConfigs, treeConfig, instanceId, elementRegistry);
      }
    });
    return () => cancelAnimationFrame(frameId);
  }, [edgeConfigs, containerBounds, treeConfig, instanceId, elementRegistry]);

  const registerElement = useCallback(
    (key: string, el: Element | null) => {
      if (el) elementRegistry.set(key, el);
      else elementRegistry.delete(key);
    },
    [elementRegistry],
  );

  const renderEdge = useCallback(
    (config: EdgeConfig, index: number) => {
      const edgeStyle = config.edgeStyle;
      return (
        <Fragment key={`${config.from}-${config.to}-${index}`}>
          <path
            ref={(el) => registerElement(`edge_path_${instanceId}_${index}`, el)}
            data-testid={`edge_path${index}`}
            d="M0 0"
            stroke={edgeStyle?.color ?? Colors.Neutral300}
            fill="none"
            strokeWidth={`${edgeStyle?.thickness ?? DEF_EDGE_THICKNESS}px`}
            role="presentation"
          />
          {config.label && (
            <foreignObject
              ref={(el) => registerElement(`label_container_${instanceId}_${index}`, el)}
              data-testid={`label_container${index}`}
              x="0"
              y="0"
              width="0"
              height="0"
              className={EdgeLabelObject}
            >
              <div
                ref={(el) => registerElement(`label_wrapper_${instanceId}_${index}`, el)}
                data-testid={`label_wrapper${index}`}
                className={EdgeLabelWrapper}
              >
                {typeof config.label === "string" ? (
                  <Text variant="strong-sm" className={EdgeLabel}>
                    {config.label}
                  </Text>
                ) : (
                  config.label
                )}
              </div>
            </foreignObject>
          )}
        </Fragment>
      );
    },
    [instanceId, registerElement],
  );

  return (
    <div className={SVGContainer} data-testid="edge-svg-container">
      <svg ref={svgElement} data-testid="edge-svg">
        {edgeConfigs.map(renderEdge)}
      </svg>
    </div>
  );
};
